import React, { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { OrderSchemaType } from '../order.schema';
import { computeItemPendencies } from '../Hooks/useItemPricePendencies';
import { Bar, Steps, ItemsMenu, ItemsMenuEntry, ItemsMenuStatus, StepButton } from './OrderStickyStepper.style';

const formatCurrency = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

export const ORDER_SECTION_IDS = {
  main: 'section-main',
  items: 'section-items',
  shipping: 'section-shipping',
  payment: 'section-payment',
  review: 'section-review',
} as const;

export const orderItemCardId = (index: number) => `order-item-card-${index}`;

/** Evento ouvido no AddOrder/index (dono do estado de colapso) para expandir o card ao navegar. */
export const EXPAND_ITEM_EVENT = 'order:expand-item';

const OrderStickyStepper: React.FC = () => {
  const { watch } = useFormContext<OrderSchemaType>();
  const items = watch('items') || [];
  const [active, setActive] = useState<keyof typeof ORDER_SECTION_IDS>('main');
  const [itemsMenuOpen, setItemsMenuOpen] = useState(false);

  const steps: { key: keyof typeof ORDER_SECTION_IDS; label: string }[] = [
    { key: 'main', label: '1 · Dados' },
    { key: 'items', label: `2 · Itens (${items.filter((it) => it.isSaved).length}) ▾` },
    { key: 'review', label: '3 · Revisão' },
    { key: 'shipping', label: '4 · Frete' },
    { key: 'payment', label: '5 · Pagamento' },
  ];

  // Sem useMemo: RHF mantém a referência do array em setValue aninhado — memo por
  // referência congelaria o menu (mesmo bug dos totais; teste E2E 2026-07-08).
  const itemMenuEntries = items.map((it: any, index: number) => {
    const tipo = { MATERIAL: 'Matéria Prima', PRODUCT: 'Produto Final', RESOURCE: 'Insumo', SERVICE: 'Serviço' }[it?.itemType as string] || 'Item';
    const nome = it?.nomeCustomizado || tipo;
    const locked = computeItemPendencies(it).length > 0;
    const efetivo = it?.priceDetails?.sellingPrice ?? it?.itemPrice;
    return {
      index,
      label: `${index + 1} · ${nome}`,
      status: locked ? '⚠ pendências' : (efetivo != null && Number(efetivo) > 0 ? formatCurrency(Number(efetivo)) : '—'),
      locked,
    };
  });

  const goToItem = (index: number) => {
    setItemsMenuOpen(false);
    window.dispatchEvent(new CustomEvent(EXPAND_ITEM_EVENT, { detail: { index } }));
    // Espera o card expandir antes de rolar.
    setTimeout(() => {
      document.getElementById(orderItemCardId(index))?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  // Scrollspy por listener de scroll em FASE DE CAPTURA (document, true): o scroll da
  // aplicação acontece num container interno do layout, e a captura enxerga qualquer
  // container. Recalcula por retângulo a cada scroll/resize — robusto a mudanças de
  // altura do DOM (abrir/fechar cards), que deixavam o IntersectionObserver com estado
  // velho (teste E2E 2026-07-08).
  useEffect(() => {
    const keys = Object.keys(ORDER_SECTION_IDS) as (keyof typeof ORDER_SECTION_IDS)[];

    const compute = () => {
      let best: keyof typeof ORDER_SECTION_IDS | null = null;
      let bestDist = Infinity;
      keys.forEach((k) => {
        const el = document.getElementById(ORDER_SECTION_IDS[k]);
        if (!el) return;
        const rect = el.getBoundingClientRect();
        if (rect.bottom < 80) return; // seção já passou por completo
        // Ativa a seção cujo topo está mais perto da faixa de leitura (~100px) —
        // distância absoluta, para seções curtas no fim da página também vencerem.
        const dist = Math.abs(rect.top - 100);
        if (dist < bestDist) {
          bestDist = dist;
          best = k;
        }
      });
      if (best) setActive(best);
    };

    compute();
    document.addEventListener('scroll', compute, true);
    window.addEventListener('resize', compute);
    return () => {
      document.removeEventListener('scroll', compute, true);
      window.removeEventListener('resize', compute);
    };
  }, []);

  const goTo = (key: keyof typeof ORDER_SECTION_IDS) => {
    if (key === 'items' && items.length > 0) {
      setItemsMenuOpen((open) => !open);
      return;
    }
    setItemsMenuOpen(false);
    setActive(key); // feedback imediato; o scrollspy assume após a rolagem
    document.getElementById(ORDER_SECTION_IDS[key])?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <Bar>
      <Steps>
        {steps.map((s) => (
          <StepButton key={s.key} type="button" active={active === s.key} onClick={() => goTo(s.key)}>
            {s.label}
          </StepButton>
        ))}
        {itemsMenuOpen && (
          <ItemsMenu>
            {itemMenuEntries.map((entry) => (
              <ItemsMenuEntry key={entry.index} type="button" onClick={() => goToItem(entry.index)}>
                <span>{entry.label}</span>
                <ItemsMenuStatus locked={entry.locked}>{entry.status}</ItemsMenuStatus>
              </ItemsMenuEntry>
            ))}
          </ItemsMenu>
        )}
      </Steps>
    </Bar>
  );
};

export default OrderStickyStepper;
